import { createSlice } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from 'src/app/redux/createAction';
import { apiRequest } from 'src/shared/api/api';
import { RequestStatus, UrlAPI } from 'src/shared/api/types';
import { CountryOverviewDTO, NameWithValue } from './resTypes';
import { PerformanceDTO } from 'src/shared/api/DTO';

const NAME = 'country';

// * Async thunks
export const getOverview = createAppAsyncThunk(`${NAME}/getOverview`, async (arg: { params: { id: number } }, thunkAPI) => {
	const { params } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<CountryOverviewDTO>({
		url: `${UrlAPI.country}/overview`,
		params,
		thunkAPI,
		signal,
	});
});

export const getPerformance = createAppAsyncThunk(`${NAME}/getPerformance`, async (arg: { params: { id: number } }, thunkAPI) => {
	const { params } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<PerformanceDTO[]>({
		url: `${UrlAPI.country}/performance`,
		params,
		thunkAPI,
		signal,
	});
});

export const getSelectedData = createAppAsyncThunk(`${NAME}/getSelectedData`, async (arg: { params: { id: number } }, thunkAPI) => {
	const { params } = arg;
	const { signal } = thunkAPI;

	return await apiRequest.getRequest<NameWithValue[]>({
		url: `${UrlAPI.country}/selecteddata`,
		params,
		thunkAPI,
		signal,
	});
});

// * Reducer
export interface State {
	overview: {
		data: CountryOverviewDTO | null;
		status: RequestStatus;
	};
	performance: {
		data: PerformanceDTO[] | null;
		status: RequestStatus;
	};
	selectedData: {
		data: NameWithValue[] | null;
		status: RequestStatus;
	};
	pdfImages: Record<string, string> | null;
	imagesLoading: number;
}

export const initialState: State = {
	overview: {
		data: null,
		status: RequestStatus.still,
	},
	performance: {
		data: null,
		status: RequestStatus.still,
	},
	selectedData: {
		data: [],
		status: RequestStatus.still,
	},
	pdfImages: null,
	imagesLoading: 0,
};

export const slice = createSlice({
	name: NAME,
	initialState,
	reducers: {
		imageLoadStart: (state) => {
			state.imagesLoading = state.imagesLoading + 1;
		},
		imageSave: (state, action: { payload: { title: string; image: string } }) => {
			const { title, image } = action.payload;

			state.pdfImages = {
				...state.pdfImages,
				[title]: image,
			};
			state.imagesLoading = state.imagesLoading - 1;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getOverview.pending, (state) => {
			state.overview.status = RequestStatus.loading;
		});
		builder.addCase(getOverview.fulfilled, (state, action) => {
			state.overview.status = RequestStatus.still;
			state.overview.data = action.payload;
		});
		builder.addCase(getOverview.rejected, (state) => {
			state.overview.status = RequestStatus.failed;
		});

		builder.addCase(getPerformance.pending, (state) => {
			state.performance.status = RequestStatus.loading;
		});
		builder.addCase(getPerformance.fulfilled, (state, action) => {
			state.performance.status = RequestStatus.still;
			state.performance.data = action.payload;
		});
		builder.addCase(getPerformance.rejected, (state) => {
			state.performance.status = RequestStatus.failed;
		});

		builder.addCase(getSelectedData.pending, (state) => {
			state.selectedData.status = RequestStatus.loading;
		});
		builder.addCase(getSelectedData.fulfilled, (state, action) => {
			state.selectedData.status = RequestStatus.still;
			state.selectedData.data = action.payload;
		});
		builder.addCase(getSelectedData.rejected, (state) => {
			state.selectedData.status = RequestStatus.failed;
		});
	},
});

export const actionsCountry = {
	...slice.actions,
	getOverview,
	getSelectedData,
	getPerformance,
};
